import type { ExamView } from '@fall26/shared'
import { useExams } from '../api/hooks.js'
import { CourseDot } from '../components/CourseDot.js'

/**
 * Exams.
 *
 * The Bring line is the loudest thing on each card, and that is deliberate.
 * There are four different materials rules this semester — a provided equation
 * sheet, a 3x5 notecard, an 8.5x11 sheet, and "nothing, Honorlock proctored" —
 * and turning up with the wrong one costs real points in a room where you
 * cannot fix it. Room and coverage sit above it; everything else is quiet.
 */
export function Exams() {
  const exams = useExams()

  if (exams.isError) {
    return (
      <p className="rounded-[var(--radius)] border border-[var(--overdue)]/30 bg-[var(--overdue-wash)] p-4 text-sm text-[var(--overdue)]">
        Could not load your exams.
      </p>
    )
  }
  if (!exams.data) return <div className="h-64 animate-pulse rounded-[var(--radius-lg)] bg-[var(--surface-sunken)]" />

  const { upcoming, needsDate, past } = exams.data

  return (
    <section className="space-y-5">
      <h1 className="text-lg font-semibold tracking-tight text-[var(--text)]">Exams</h1>

      <Section label="Upcoming" count={upcoming.length}>
        {upcoming.map((exam) => (
          <ExamCard key={exam.item.id} exam={exam} />
        ))}
      </Section>

      {needsDate.length > 0 && (
        <Section label="Date not released" count={needsDate.length}>
          <p className="-mt-0.5 mb-1.5 text-[12px] text-[var(--text-muted)]">
            The registrar has not published these. The dates shown are provisional.
          </p>
          {needsDate.map((exam) => (
            <ExamCard key={exam.item.id} exam={exam} />
          ))}
        </Section>
      )}

      {past.length > 0 && (
        <Section label="Done" count={past.length}>
          {past.map((exam) => (
            <ExamCard key={exam.item.id} exam={exam} muted />
          ))}
        </Section>
      )}
    </section>
  )
}

function Section({
  label,
  count,
  children,
}: {
  label: string
  count: number
  children: React.ReactNode
}) {
  if (count === 0) return null
  return (
    <div>
      <div className="mb-1.5 flex items-baseline gap-2">
        <h2 className="tabular text-[11px] uppercase tracking-[0.14em] text-[var(--text-subtle)]">{label}</h2>
        <span className="tabular text-[11px] text-[var(--text-subtle)]">{count}</span>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  )
}

function ExamCard({ exam, muted = false }: { exam: ExamView; muted?: boolean }) {
  const soon = exam.daysAway !== null && exam.daysAway <= 7 && exam.daysAway >= 0

  return (
    <article
      className={`relative overflow-hidden rounded-[var(--radius-lg)] border bg-[var(--surface)] p-3.5 pl-4 ${
        soon ? 'border-[var(--accent-line)]' : 'border-[var(--border)]'
      } ${muted ? 'opacity-60' : ''}`}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{ backgroundColor: `var(--course-${exam.colorIndex % 6})` }}
      />

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <CourseDot colorIndex={exam.colorIndex} />
            <span className="tabular text-[10px] uppercase tracking-[0.1em] text-[var(--text-subtle)]">
              {exam.courseCode}
            </span>
          </div>
          <h3 className="mt-0.5 text-[15px] font-medium leading-snug text-[var(--text)]">
            {exam.item.title}
          </h3>
        </div>

        <div className="shrink-0 text-right">
          {exam.dateUnconfirmed ? (
            <span className="tabular rounded-full bg-[var(--unknown-wash)] px-2 py-0.5 text-[10px] uppercase tracking-[0.06em] text-[var(--unknown)]">
              not released
            </span>
          ) : (
            <span
              className={`tabular block text-[12px] ${soon ? 'font-semibold text-[var(--accent)]' : 'text-[var(--text-muted)]'}`}
            >
              {exam.countdown}
            </span>
          )}
          <span className="tabular mt-0.5 block text-[11px] text-[var(--text-subtle)]">
            {exam.item.display.dateTime || '—'}
          </span>
        </div>
      </div>

      <dl className="mt-2.5 space-y-1">
        {exam.room && <Field label="Room">{exam.room}</Field>}
        {exam.coverage && <Field label="Covers">{exam.coverage}</Field>}
      </dl>

      {/* The line that costs points if you get it wrong. */}
      {exam.bring && (
        <div className="mt-2.5 rounded-[var(--radius)] border border-[var(--soon)]/35 bg-[var(--soon-wash)] px-2.5 py-2">
          <div className="tabular text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--soon)]">
            Bring
          </div>
          <p className="mt-0.5 text-[13px] leading-snug text-[var(--text)]">{exam.bring}</p>
        </div>
      )}
    </article>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="tabular w-14 shrink-0 text-[11px] uppercase tracking-[0.06em] text-[var(--text-subtle)]">
        {label}
      </dt>
      <dd className="min-w-0 text-[12.5px] leading-snug text-[var(--text-muted)]">{children}</dd>
    </div>
  )
}
